import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, CheckCircle2, Cloud, BarChart2, TrendingUp, DollarSign,
  ShieldCheck, Clock, Award, Zap, Database, Settings
} from 'lucide-react';

const FMCGAI = () => {
  const metrics = [
    { value: '40%', label: 'Cost Reduction', description: 'Infrastructure & operational overhead' },
    { value: '25%', label: 'Faster Processing', description: 'ETL & pipeline performance' },
    { value: '98%+', label: 'Migration Success', description: 'Stored procedures & pipelines' },
    { value: '99.9%', label: 'Uptime', description: 'Post-cutover production environments' },
  ];

  const services = [
    {
      icon: Cloud,
      title: 'Cloud Migration & Infrastructure Modernisation',
      description: 'Lift-and-shift plus modernise — migrate your on-premises SQL infrastructure to Azure with zero data loss and minimal downtime.',
      outcomes: [
        'On-premises to Azure end-to-end migration planning and execution',
        'Azure Data Factory for ETL pipeline migration and orchestration',
        'Azure SQL Managed Instance migration (200+ stored procedures)',
        'High Availability architecture with geo-redundant failover',
      ],
    },
    {
      icon: BarChart2,
      title: 'Reporting & BI Transformation',
      description: 'Replace legacy SSRS with modern Power BI paginated and interactive reports — with automated distribution and executive dashboards.',
      outcomes: [
        'SSRS to Power BI migration (1,000+ reports)',
        'Automated report distribution via Power Automate',
        'Executive dashboards with real-time KPIs',
        'Multi-tenant row-level security with Azure AD',
      ],
    },
    {
      icon: TrendingUp,
      title: 'Demand Forecasting & Inventory AI',
      description: 'ML-powered demand intelligence that accounts for seasonality, promotions, and external signals to reduce stockouts and overstock.',
      outcomes: [
        'XGBoost and ensemble ML demand forecasting models',
        'Inventory optimisation with safety stock modelling',
        'Seasonal and promotional uplift modelling',
        'Supply chain visibility dashboards and alerting',
      ],
    },
    {
      icon: DollarSign,
      title: 'Financial & Commission Automation',
      description: 'Automate complex commission calculations, tax compliance, and merchant reconciliation — with full audit trails.',
      outcomes: [
        'Commission calculation engine (volume, margin, tier-based)',
        'GST, VAT, and TDS compliance automation',
        'Merchant reporting and statement generation',
        'Reconciliation automation with exception management',
      ],
    },
  ];

  const techStack = [
    { category: 'Cloud & Migration', items: ['Azure Data Factory', 'Azure SQL Managed Instance', 'Azure Data Lake'] },
    { category: 'BI & Reporting', items: ['Power BI Paginated', 'Tableau', 'Power Automate'] },
    { category: 'ML & Analytics', items: ['Python', 'XGBoost', 'scikit-learn'] },
    { category: 'Orchestration', items: ['Apache Airflow', 'Snowflake', 'dbt'] },
  ];

  const useCases = [
    { name: 'Legacy Infrastructure Modernisation', desc: 'On-prem SQL Server to Azure cloud with all stored procedures, jobs, and linked servers migrated intact' },
    { name: 'Operational Reporting Gaps', desc: '1,000+ SSRS reports converted to Power BI with automated delivery and role-based access control' },
    { name: 'Demand & Supply Chain Intelligence', desc: 'ML-driven forecasting models integrated with ERP/WMS for live inventory optimisation' },
    { name: 'Financial Process Automation', desc: 'Commission engines, tax compliance modules, and reconciliation workflows replacing manual Excel processes' },
  ];

  const whyChoose = [
    {
      icon: Award,
      title: 'Enterprise-Scale Migration Experience',
      description: '200+ stored procedures, 1,000+ reports — we have done it at scale and delivered on time.',
    },
    {
      icon: ShieldCheck,
      title: 'Zero Disruption to Operations',
      description: 'Parallel validation, phased cutover, and rollback plans ensure business continuity throughout.',
    },
    {
      icon: BarChart2,
      title: 'Finance-Ready Reporting',
      description: 'Commission and compliance reporting built to pass finance audit — not just look good in a demo.',
    },
    {
      icon: Zap,
      title: 'Full Stack Delivery',
      description: 'Data engineering, ML, BI, and automation — one team, one engagement, no coordination overhead.',
    },
  ];

  const faqs = [
    {
      q: 'How do you handle complex T-SQL stored procedures during migration?',
      a: 'We run a full compatibility analysis before migration begins, mapping each procedure to Azure SQL Managed Instance equivalents. We test in parallel environments and validate outputs against the source system before cutover. Our 98%+ success rate reflects this rigorous process.',
    },
    {
      q: 'Can you migrate SSIS packages to Azure Data Factory?',
      a: 'Yes, SSIS-to-ADF migration is a core competency. We handle IR (Integration Runtime) configuration, package conversion, and self-hosted integration runtime setup for on-premises data source connectivity.',
    },
    {
      q: 'How do you manage a migration of 1,000+ reports?',
      a: 'We start with a report inventory and criticality assessment, then prioritise by business usage. RDL to Power BI conversion is handled in batches with validation sign-off at each stage. Unused or redundant reports are rationalised before migration, not after.',
    },
    {
      q: 'What about multi-tenant security in Power BI?',
      a: 'Row-Level Security configured with Azure Active Directory groups, Key Vault for credential management, and private endpoints as standard. Each tenant sees only their data — enforced at the dataset level, not the report level.',
    },
    {
      q: 'Do you support post-migration managed delivery?',
      a: 'Yes. We offer ongoing managed delivery agreements covering pipeline monitoring, report enhancements, model retraining, and platform expansion as your business grows.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>AI & Data Solutions for FMCG Enterprises | Agility</title>
        <meta
          name="description"
          content="Cloud migration, demand forecasting, and BI transformation for FMCG businesses. Azure migration specialists — 200+ stored procedures, 1,000+ reports, zero data loss."
        />
        <link rel="canonical" href="https://www.agilitytech.ai/fmcg-ai" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI & Data Solutions for FMCG Enterprises | Agility" />
        <meta property="og:description" content="Cloud migration, demand forecasting, and BI transformation for FMCG businesses. Azure migration specialists — 200+ stored procedures, 1,000+ reports, zero data loss." />
        <meta property="og:url" content="https://www.agilitytech.ai/fmcg-ai" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-900 via-black to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-emerald-500 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full mb-8">
            <Database className="w-4 h-4 text-emerald-400" />
            <span className="text-emerald-300 text-sm font-medium">FMCG AI & Data Solutions</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI & Data for<br />
            <span className="text-teal-400">FMCG Enterprises</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Cloud migration, demand forecasting, and BI transformation built for FMCG complexity.
            200+ stored procedures, 1,000+ reports, zero data loss — delivered on schedule.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Schedule a Discovery Call
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all"
            >
              View Case Studies
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {metrics.map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-1">{m.value}</div>
                <div className="text-white font-semibold mb-1">{m.label}</div>
                <div className="text-gray-400 text-sm">{m.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What We Build for FMCG
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Four specialist service areas targeting the most expensive problems in FMCG data and operations.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                  <Icon className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-600 mb-5 text-sm leading-relaxed">{s.description}</p>
                  <ul className="space-y-2">
                    {s.outcomes.map((o, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Result */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-emerald-700 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Featured FMCG Delivery</h2>
          <p className="text-teal-100 text-lg mb-10 max-w-2xl mx-auto">
            A leading FMCG distributor migrated from on-premises SQL Server to Azure — fully modernised in 20 weeks.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '200+', label: 'Stored Procedures Migrated' },
              { value: '1,000+', label: 'SSRS Reports Converted' },
              { value: '40%', label: 'Cost Reduction Achieved' },
              { value: '20 wks', label: 'End-to-End Delivery' },
            ].map((stat, i) => (
              <div key={i} className="bg-white/10 rounded-xl p-5 border border-white/20">
                <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-teal-100 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technology Stack</h2>
            <p className="text-lg text-gray-600">Azure-first with the modern data stack for FMCG scale.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {techStack.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">{t.category}</div>
                <ul className="space-y-2">
                  {t.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-800 text-sm font-medium">
                      <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Common FMCG Use Cases</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Concrete problems we solve — not whitepapers.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((uc, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="text-teal-700 font-bold mb-2">{uc.name}</div>
                <p className="text-gray-600 text-sm leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why FMCG Leaders Choose <span className="text-teal-400">Agility</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoose.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white/5 rounded-xl p-8 border border-white/10 hover:border-teal-500/50 transition-all">
                  <Icon className="w-10 h-10 text-teal-400 mb-4" />
                  <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl p-8 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{f.q}</h3>
                <p className="text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-emerald-700 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Modernise Your FMCG Data Stack?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Tell us your migration or analytics challenge. We'll scope a plan in 48 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-emerald-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Schedule a Discovery Call
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default FMCGAI;
